<template>
	<div>
		<!-- Headline -->
		<div slot="headline" class="flex xs-12 fs5 lh125 fw7 c1 mb2">
			<div class="flex xs-6">{{ showTitle ? $t("payment_info") : "" }}</div>
			<div v-if="computedList.length > 0" class="flex xs-6 xs-right x2">
				<j-button v-if="canWrite" class="tertiary" @click="handleAdd">
					<crm-icon name="add" size="normal" color="c2" />
					{{ $t("add_payment_info") }}
				</j-button>
			</div>
		</div>

		<!-- Items -->
		<j-expendable-box
			v-for="(item, i) in computedList"
			:key="i"
			class="bg5 pl3 fs3 mt2 r2 b1 bc7"
		>
			<div slot="header" class="c2 fw7">{{ item.name || "-----" }}</div>
			<div slot="body" class="flex wrap xs-12 pb4 pr4 mt4">
				<div v-if="item.payor" class="flex xs-12 mt6">
					<div class="flex xs-6 c6 fw7">{{ $t("payor_name") }}</div>
					<div class="flex xs-6 xs-right">{{ item.payor.name }}</div>
				</div>
				<div v-if="item.payor" class="flex xs-12 mt6">
					<div class="flex xs-6 c6 fw7">{{ $t("payor_citizen_id") }}</div>
					<div class="flex xs-6 xs-right">
						{{ item.payor.identities[0].value }}
					</div>
				</div>
				<div class="flex xs-12 mt6">
					<div class="flex xs-6 c6 fw7">{{ $t("amount") }}</div>
					<div class="flex xs-6 xs-right">{{ item.amount }}</div>
				</div>
				<div class="flex xs-12 mt6">
					<div class="flex xs-6 c6 fw7">{{ $t("comment") }}</div>
					<div class="flex xs-6 xs-right">{{ item.comment }}</div>
				</div>
				<div class="flex xs-12 mt6">
					<div class="flex xs-6 c6 fw7">{{ $t("currency") }}</div>
					<div class="flex xs-6 xs-right">
						{{ $t(`currencies.${item.currencyCode}`) }}
					</div>
				</div>
				<div class="flex xs-12 mt6">
					<div class="flex xs-6 c6 fw7">{{ $t("payment_method") }}</div>
					<div class="flex xs-6 xs-right">
						{{ $t(`payment_methods_enums.${item.method}`) }}
					</div>
				</div>
				<div class="flex xs-12 mt6">
					<div class="flex xs-6 c6 fw7">{{ $t("payment_frequency") }}</div>
					<div class="flex xs-6 xs-right">
						{{ $t(`payment_frequency_enums.${item.frequency}`) }}
					</div>
				</div>
				<div class="flex xs-12 xs-right mt6 bt1 bc7">
					<j-button
						v-if="canWrite"
						class="primary mt2"
						size="small"
						@click="handleUpdate(item)"
						>{{ $t("update") }}</j-button
					>
					<j-button
						v-if="canWrite"
						class="error mt2 ml2"
						size="small"
						@click="handleRemove(item.id)"
						>{{ $t("remove") }}</j-button
					>
				</div>
			</div>
		</j-expendable-box>

		<!-- Empty -->
		<j-card class="bg5" v-if="computedList.length === 0">
			<div class="flex xs-12 xs-center xs-middle p2 ta2 fs3">
				<j-button
					v-if="canWrite"
					class="primary"
					size="xsmall"
					@click="handleAdd"
					>{{ $t("add_payment_info") }}</j-button
				>
			</div>
		</j-card>

		<!-- Modal -->
		<core-modal name="addUpdatePaymentMethod" :headline="headline">
			<add-update-payment-infos
				:type="type"
				v-bind="form"
				@added="afterAdd"
				@updated="afterUpdate"
				@cancelled="$modal.hide('addUpdatePaymentMethod')"
			/>
		</core-modal>
	</div>
</template>

<script>
import gql from "@/api/gql.js"
import JCard from "@/components/JCard"
import JExpendableBox from "@/components/JExpendableBox"
import JButton from "@/components/JButton"
import CrmIcon from "@/components/CrmIcon"
import CoreModal from "@/modules/core/components/CoreModal"
import AddUpdatePaymentInfos from "./AddUpdatePaymentInfos"

export default {
	components: {
		JCard,
		JButton,
		CrmIcon,
		CoreModal,
		AddUpdatePaymentInfos,
		JExpendableBox
	},

	data() {
		return {
			headline: "",
			type: "", // add or update payment method
			form: {}
		}
	},

	props: {
		list: { type: Array, required: true, default: () => [] },
		showTitle: {
			type: Boolean,
			required: false,
			default: true
		},
		canWrite: {
			type: Boolean,
			required: false,
			default: true
		}
	},

	computed: {
		computedList() {
			return this.list.map(item => ({
				id: item.id,
				payor: item.payor,
				name: item.name,
				comment: item.comment,
				amount: item.amount,
				currencyCode: item.currencyCode,
				method: item.method,
				frequency: item.frequency
			}))
		}
	},

	methods: {
		handleAdd() {
			this.type = "add"
			this.headline = this.$t("add_payment_info")
			this.$modal.show("addUpdatePaymentMethod")
		},

		handleUpdate(payload) {
			this.type = "update"
			this.form = { ...payload }
			this.headline = this.$t("update_payment_info")
			this.$modal.show("addUpdatePaymentMethod")
		},

		async apiRemovePaymentInfo(variables) {
			const mutation = `
        mutation removePaymentInfo($caseId: String! $paymentInfoId: String!){
          removePaymentInfoFromCase(caseId: $caseId, paymentInfoId: $paymentInfoId){
                    status
              }
        }
      `
			const res = await gql({
				query: mutation,
				variables
			})
      return Promise.resolve(res)
		},

		async handleRemove(payload) {
			const caseId = this.$route.params.id
			const paymentInfoId = payload

			this.isLoading = true

			const res = await this.apiRemovePaymentInfo({ paymentInfoId, caseId })

			this.isLoading = false

			if (res.errors) {
				this.$store.dispatch("showError", res.errors)
				return
			}

			this.$emit("removed")
		},

		afterAdd() {
			this.$modal.hide("addUpdatePaymentMethod")
			this.$emit("added")
		},

		afterUpdate() {
			this.$modal.hide("addUpdatePaymentMethod")
			this.$emit("updated")
		}
	}
}
</script>
